import React, { useState, useEffect, useCallback, useRef } from 'react';

import MenuModal from '../../navigation/ui/Main';
import UserModal from '../../modals/user_modal/ui/Main';
import SearchModal from '../../modals/search_modal/ui/Main';
import LanguageModal from '../../modals/language_modal/ui/Main';

import app_icon from '../../../app_icon.svg';
import user_icon from '../../../assets/icons/user.svg';

import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../../language/i18n';
import { getCountries } from '../../../api/location/getCountries';
import { getRegions } from '../../../api/location/getRegions';
import { getLocalizedName } from '../../../api/location/locationUtils';

import Icon from '../../../common/components/Icon';

function Header({ onToggleMenu = () => { } }) {
    const [menuVisibility, setMenuVisibility] = useState({
        menu: false,
        user: false,
        address: false,
        search: false,
        language: false,
    });

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('language') || 'kz');
    const [, setCountries] = useState([]);
    const [, setRegions] = useState([]);
    const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
    const locationMenuRef = useRef(null);

    useEffect(() => {
        i18n.changeLanguage(currentLanguage);
    }, [currentLanguage, i18n]);
    useEffect(() => {
        async function fetchLocations() {
            try {
                const countriesData = await getCountries();
                const regionsData = await getRegions();
                
                // Ensure required countries are present (add if missing)
                const requiredCountryNames = {
                    kz: ['Қазақстан', 'Өзбекстан', 'Қытай', 'Қырғызстан'],
                    ru: ['Казахстан', 'Узбекистан', 'Китай', 'Кыргызстан'],
                    en: ['Kazakhstan', 'Uzbekistan', 'China', 'Kyrgyzstan'],
                    cn: ['哈萨克斯坦', '乌兹别克斯坦', '中国', '吉尔吉斯斯坦']
                };
                
                const currentLangKey = currentLanguage === 'kz' ? 'kz' : currentLanguage === 'ru' ? 'ru' : currentLanguage === 'en' ? 'en' : 'cn';
                const requiredNames = requiredCountryNames[currentLangKey] || requiredCountryNames['ru'];
                
                // Check if required countries exist, add them if missing
                const existingCountryNames = countriesData.map(c => getLocalizedName(c, currentLanguage));
                const missingCountries = [];
                
                requiredNames.forEach((name, index) => {
                    if (!existingCountryNames.includes(name)) {
                        // Create a placeholder country object
                        missingCountries.push({
                            id: 1000 + index, // Temporary ID
                            name_kz: requiredCountryNames.kz[index],
                            name_ru: requiredCountryNames.ru[index],
                            name_en: requiredCountryNames.en[index],
                            name_zh: requiredCountryNames.cn[index],
                            name_cn: requiredCountryNames.cn[index]
                        });
                    }
                });
                
                // Combine API countries with missing ones
                setCountries([...countriesData, ...missingCountries]);
                setRegions(regionsData);
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        }
        fetchLocations();
    }, [currentLanguage]);

    // Close location menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locationMenuRef.current && !locationMenuRef.current.contains(event.target)) {
                setIsLocationMenuOpen(false);
            }
        };

        if (isLocationMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLocationMenuOpen]);


    const changeLanguage = (lng) => {
        setCurrentLanguage(lng);
        localStorage.setItem('language', lng);
        i18n.changeLanguage(lng);
    };

    const toggleMenu = useCallback(
        (menuName) => {
            if (menuName === 'user') {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    navigate('/sign');
                    return;
                }
            }
            setMenuVisibility((prev) => {
                const newMenuState = !prev[menuName];
                if (menuName === 'menu') {
                    onToggleMenu(newMenuState);
                }
                return {
                    ...prev,
                    [menuName]: newMenuState,
                };
            });
        },
        [navigate, onToggleMenu]
    );

    return (
        <header className="bg-white sticky top-0 w-full z-[90] h-[90px] md:h-[60px]">
            <div className="flex items-center justify-between px-4 md:px-[60px] lg:px-[120px] xl:px-[240px] h-[70px] md:h-[60px] w-full mx-auto">
                <div className="flex items-center gap-4 md:gap-6">
                    <Link to="/">
                            <img src={app_icon} alt="Agroland" className="h-[50px] xl:h-[40px] md:h-[30px]" />
                    </Link>
                    <div className="hidden md:flex items-center gap-4 relative">
                        <button
                            className="cursor-pointer bg-white rounded-full border-2 border-[#EDEDED] p-3 flex items-center justify-center w-12 h-12 hover:bg-[#F9F9F9] flex-shrink-0"
                            onClick={() => toggleMenu('menu')}
                        >
                            <Icon src="bar" alt="menu" className="w-6 h-6" />
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="relative">
                        <button className="cursor-pointer bg-white rounded-full border-2 border-[#147F26] p-2 md:p-3 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 hover:bg-[#F9F9F9]" onClick={() => toggleMenu('language')}>
                            <Icon src="glob" alt="change_language" className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                        <LanguageModal
                            menuVisible={menuVisibility.language}
                            toggleMenu={() => toggleMenu('language')}
                            onSelectLanguage={changeLanguage}
                        />
                    </div>
                    <button className="cursor-pointer bg-white rounded-full md:border-2 md:border-[#147F26] p-2 md:p-3 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 hover:bg-[#F9F9F9]" onClick={() => toggleMenu('search')}>
                        <Icon src="search" alt="search" className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                    <button
                        onClick={() => toggleMenu('menu')}
                        className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-[#147F26] transition-all duration-300"
                        >
                        {menuVisibility.menu ? (
                            <div className="relative w-6 h-6">
                            <span className="absolute top-1/2 left-0 w-6 h-[2px] bg-white rotate-45 -translate-y-1/2"></span>
                            <span className="absolute top-1/2 left-0 w-6 h-[2px] bg-white -rotate-45 -translate-y-1/2"></span>
                            </div>
                        ) : (
                            <Icon
                            src="bar"
                            alt="menu"
                            className="w-6 h-6 brightness-0 invert"
                            />
                        )}
                        </button>
                    <button className="hidden md:block bg-[#147F26] text-white px-6 py-3 rounded-[10px] cursor-pointer h-[50px] border-none text-base font-medium hover:bg-[#7AB30E] whitespace-nowrap" onClick={() => navigate('/adform')}>
                        {t('createAnnouncement')}
                </button>
                <div className="relative">
                    <button
                        className="cursor-pointer hidden md:flex border-none bg-[#147F26] text-white rounded-full h-[40px] w-[40px] hover:bg-[#7AB30E] items-center justify-center"
                        onClick={() => toggleMenu('user')}
                    >
                        <img src={user_icon} alt="user_icon" className="w-6 h-6" />
                    </button>
                    <UserModal menuVisible={menuVisibility.user} toggleMenu={() => toggleMenu('user')} />
                </div>
                </div>
            </div>
            <MenuModal menuVisible={menuVisibility.menu} toggleMenu={() => toggleMenu('menu')} />
            <SearchModal menuVisible={menuVisibility.search} toggleMenu={() => toggleMenu('search')} />
        </header>
    );
}

export default Header;
